// pages/UserProfile.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  Avatar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import SaveIcon from '@mui/icons-material/Save';
import Select from "react-select";
import axiosInstance from "../../utils/axiosInstance";
import ResetPassword from "./UserProfile/ResetPassword";
import AccountConversion from "./UserProfile/AccountConversion";
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  accent: '#3b82f6',
  success: '#059669',
  error: '#dc2626',
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

export default function VerticalTabs() {
  const navigate = useNavigate();
  const [value, setValue] = React.useState(0);

  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const stateFetchIdRef = useRef(0);
  const stateSearchTimeoutRef = useRef(null);
  const cityFetchIdRef = useRef(0);
  const citySearchTimeoutRef = useRef(null);
  const selectedCountryIdRef = useRef(null);
  const selectedStateRef = useRef(null);

  const [UserProfile, setUserProfile] = useState("");
  const [UserfName, setUserfName] = useState("");
  const [UserlName, setUserlName] = useState("");
  const [UserEmail, setUserEmail] = useState("");
  const [UserAddress, setUserAddress] = useState("");
  const [UserCountry, setUserCountry] = useState("");
  const [UserCity, setUserCity] = useState("");
  const [UserState, setUserState] = useState("");
  const [UserZip, setUserZip] = useState("");
  const [UserTimezone, setUserTimezone] = useState("UTC");
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    document.title = "User Profile | KlonIT Web Application";

    const fetchSelections = async () => {
      try {
        const [countriesRes, timezonesRes] = await Promise.all([
          axiosInstance.get(`/api/selections/countries`),
          axiosInstance.get(`/api/selections/timezones`),
        ]);
        setCountryOptions(countriesRes.data.data);
        setTimezoneOptions(timezonesRes.data.data);
      } catch (error) {
        console.error("Failed to fetch selection data:", error);
        setMessage("Error: Could not load country and timezone lists.");
      }
    };

    const fetchProfileData = () => {
      axiosInstance
        .get(`/get-user-profile`)
        .then((response) => {
          const responseData = response.data.data;
          if (responseData && responseData.length > 0) {
            let profile_data = responseData[0];
            setUserProfile(profile_data.profile_image);
            setUserfName(profile_data.fname);
            setUserlName(profile_data.lname);
            setUserEmail(profile_data.email);
            setUserAddress(profile_data.address_one);
            setUserCountry(profile_data.country);
            setUserCity(profile_data.city);
            setUserState(profile_data.state);
            setUserZip(profile_data.zip);
            setUserTimezone(profile_data.timezone || "UTC");
          } else {
            const user = JSON.parse(localStorage.getItem("user"));
            if (user && user.user_email) {
              setUserEmail(user.user_email);
            }
          }
        })
        .catch((error) => {
          setMessage("Error fetching for User Profile:", error);
        });
    };

    fetchSelections();
    fetchProfileData();
  }, []);

  const handleChange = (event, newValue) => setValue(newValue);

  const fetchStatesByCountry = useCallback(async (countryId, searchTerm = '') => {
    if (!countryId) {
      setStateOptions([]);
      return;
    }
    const fetchId = ++stateFetchIdRef.current;
    setLoadingStates(true);
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await axiosInstance.get(`/api/selections/countries/${countryId}/states`, { params });
      if (fetchId === stateFetchIdRef.current && response.data.success) {
        setStateOptions(response.data.data || []);
      }
    } catch (error) {
      if (fetchId === stateFetchIdRef.current) {
        setStateOptions([]);
      }
    } finally {
      if (fetchId === stateFetchIdRef.current) {
        setLoadingStates(false);
      }
    }
  }, []);

  const fetchCitiesByCountry = useCallback(async (countryId, searchTerm = '', stateFilter = '') => {
    if (!countryId) {
      setCityOptions([]);
      return;
    }
    const fetchId = ++cityFetchIdRef.current;
    setLoadingCities(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (stateFilter) params.state = stateFilter;
      const response = await axiosInstance.get(`/api/selections/countries/${countryId}/cities`, { params });
      if (fetchId === cityFetchIdRef.current && response.data.success) {
        setCityOptions(response.data.data || []);
      }
    } catch (error) {
      if (fetchId === cityFetchIdRef.current) {
        setCityOptions([]);
      }
    } finally {
      if (fetchId === cityFetchIdRef.current) {
        setLoadingCities(false);
      }
    }
  }, []);

  const triggerStateSearch = useCallback((searchTerm) => {
    if (stateSearchTimeoutRef.current) clearTimeout(stateSearchTimeoutRef.current);
    const countryId = selectedCountryIdRef.current;
    if (!countryId) return;
    if (searchTerm.length > 0) {
      stateFetchIdRef.current++;
      setStateOptions([]);
      setLoadingStates(true);
    }
    stateSearchTimeoutRef.current = setTimeout(() => {
      fetchStatesByCountry(countryId, searchTerm);
    }, 150);
  }, [fetchStatesByCountry]);

  const triggerCitySearch = useCallback((searchTerm) => {
    if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
    const countryId = selectedCountryIdRef.current;
    const stateFilter = selectedStateRef.current;
    if (!countryId) return;
    if (searchTerm.length > 0) {
      cityFetchIdRef.current++;
      setCityOptions([]);
      setLoadingCities(true);
    }
    citySearchTimeoutRef.current = setTimeout(() => {
      fetchCitiesByCountry(countryId, searchTerm, stateFilter);
    }, 150);
  }, [fetchCitiesByCountry]);

  useEffect(() => {
    return () => {
      if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
      if (stateSearchTimeoutRef.current) clearTimeout(stateSearchTimeoutRef.current);
    };
  }, []);

  const handleCountryChange = (selectedOption) => {
    setUserCountry(selectedOption ? selectedOption.value : "");
    setUserState("");
    setUserCity("");
    if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
    if (stateSearchTimeoutRef.current) clearTimeout(stateSearchTimeoutRef.current);
    selectedCountryIdRef.current = selectedOption?.id || null;
    selectedStateRef.current = null;
    if (selectedOption?.id) {
      fetchStatesByCountry(selectedOption.id, '');
      setCityOptions([]);
    } else {
      setStateOptions([]);
      setCityOptions([]);
    }
  };

  const handleStateChange = (selectedOption) => {
    setUserState(selectedOption ? selectedOption.value : "");
    setUserCity("");
    if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
    selectedStateRef.current = selectedOption?.value || null;
    const countryId = selectedCountryIdRef.current;
    if (countryId && selectedOption?.value) {
      fetchCitiesByCountry(countryId, '', selectedOption.value);
    } else if (countryId) {
      fetchCitiesByCountry(countryId, '', '');
    } else {
      setCityOptions([]);
    }
  };

  const handleCityChange = (selectedOption) => setUserCity(selectedOption ? selectedOption.label : "");
  const handleTimezoneChange = (selectedOption) => setUserTimezone(selectedOption ? selectedOption.value : "UTC");

  const getDefaultCountryOption = () => countryOptions.find((option) => option.value === UserCountry);
  const getDefaultStateOption = () => stateOptions.find((option) => option.value === UserState);
  const getDefaultCityOption = () => cityOptions.find((option) => option.label === UserCity);
  const getDefaultTimezoneOption = () => timezoneOptions.find((option) => option.value === UserTimezone);

  useEffect(() => {
    if (UserCountry && countryOptions.length > 0) {
      const countryObj = countryOptions.find((c) => c.value === UserCountry);
      if (countryObj?.id) {
        selectedCountryIdRef.current = countryObj.id;
        fetchStatesByCountry(countryObj.id, '');
      }
    }
  }, [UserCountry, countryOptions, fetchStatesByCountry]);

  useEffect(() => {
    if (UserState && stateOptions.length > 0 && selectedCountryIdRef.current) {
      selectedStateRef.current = UserState;
      fetchCitiesByCountry(selectedCountryIdRef.current, '', UserState);
    }
  }, [UserState, stateOptions, fetchCitiesByCountry]);

  const handleSaveButtonClick = async () => {
    setSaving(true);
    const commonData = {
      fname: UserfName,
      lname: UserlName,
      address_one: UserAddress,
      country: UserCountry,
      city: UserCity,
      state: UserState,
      zip: UserZip,
      timezone: UserTimezone,
    };

    if (selectedFile) {
      const validExtensions = ["image/jpeg", "image/jpg", "image/png"];
      if (!validExtensions.includes(selectedFile.type)) {
        setMessage("Invalid file type. Only JPG, JPEG, and PNG are allowed.");
        setSaving(false);
        return;
      }
      const formData = new FormData();
      formData.append("avatar", selectedFile);
      Object.keys(commonData).forEach((key) => formData.append(key, commonData[key]));
      try {
        const response = await axiosInstance.post(`/update-user-profile`, formData);
        if (response.data.success) {
          const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
          setUserProfile(responseData.profile_image);
          updateUserProfileToLocalStorage(responseData);
          setSelectedFile(null);
          setPreviewUrl(null);
        }
        setMessage(response.data.message);
      } catch (error) {
        setMessage("Error updating user profile with image.");
      }
    } else {
      const data = { ...commonData, profile_image: UserProfile };
      try {
        const response = await axiosInstance.post(`/update-user-profile`, data);
        if (response.data.success) {
          const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
          updateUserProfileToLocalStorage(responseData);
        }
        setMessage(response.data.message);
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "An unknown error occurred.";
        setMessage(errorMessage);
      }
    }
    setSaving(false);
  };

  const handleOpenDeleteDialog = () => {
    setDeleteMessage("");
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);

  const handleDeleteAccount = async () => {
    handleCloseDeleteDialog();
    setDeleteMessage("Deleting your account, please wait...");
    try {
      await axiosInstance.delete(`/delete-user-account`);
      setDeleteMessage("Your account has been successfully deleted. You will be logged out now.");
      setTimeout(() => {
        localStorage.clear();
        navigate("/log-out");
      }, 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Error: Could not delete your account. Please try again.";
      setDeleteMessage(errorMessage);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const updateUserProfileToLocalStorage = (profileData) => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      userData.profile = profileData || null;
      localStorage.setItem('user', JSON.stringify(userData));
      window.dispatchEvent(new CustomEvent('userLocalStorageUpdated', { detail: userData }));
    }
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      fontFamily,
      fontSize: '0.875rem',
      borderRadius: '10px',
      borderColor: 'rgba(0,0,0,0.1)',
      boxShadow: 'none',
      '&:hover': { borderColor: colors.accent },
    }),
    option: (base, state) => ({
      ...base,
      fontFamily,
      fontSize: '0.875rem',
      backgroundColor: state.isSelected ? colors.accent : state.isFocused ? 'rgba(59,130,246,0.1)' : 'white',
    }),
  };

  const tabItems = [
    { label: 'Account', icon: <PersonOutlineIcon sx={{ fontSize: '1.1rem' }} /> },
    { label: 'Change Password', icon: <LockOutlinedIcon sx={{ fontSize: '1.1rem' }} /> },
    { label: 'Convert Account', icon: <SwapHorizIcon sx={{ fontSize: '1.1rem' }} /> },
    { label: 'Delete Account', icon: <DeleteOutlineIcon sx={{ fontSize: '1.1rem' }} /> },
  ];

  return (
    <Box sx={{ fontFamily, maxWidth: '1100px', width: '100%', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
          boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
        }}>
          <PersonOutlineIcon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em' }}>
          User Profile
        </Typography>
        <Typography sx={{ fontFamily, fontSize: '0.875rem', color: colors.textTertiary, mt: 0.5 }}>
          Manage your account settings and preferences
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Sidebar Tabs */}
        <Grid item xs={12} md={3}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            p: 2,
            border: '1px solid rgba(0,0,0,0.06)',
          }}>
            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.08em', px: 1, mb: 1 }}>
              Settings
            </Typography>
            <Tabs
              orientation="vertical"
              value={value}
              onChange={handleChange}
              sx={{
                '& .MuiTab-root': {
                  fontFamily,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  minHeight: 44,
                  justifyContent: 'flex-start',
                  px: 2,
                  borderRadius: '10px',
                  color: colors.textSecondary,
                  '&:hover': { bgcolor: 'rgba(59,130,246,0.08)' },
                  '&.Mui-selected': {
                    color: colors.accent,
                    bgcolor: 'rgba(59,130,246,0.1)',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': { display: 'none' },
              }}
            >
              {tabItems.map((item, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {item.icon}
                      {item.label}
                    </Box>
                  }
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </Box>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            p: 3,
            border: '1px solid rgba(0,0,0,0.06)',
            minHeight: 400,
          }}>
            {/* Account Tab */}
            <TabPanel value={value} index={0}>
              <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 600, color: colors.textPrimary, mb: 3 }}>
                Account Settings
              </Typography>

              <Grid container spacing={3}>
                {/* Avatar Section */}
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      component="label"
                      sx={{
                        position: 'relative',
                        width: 110, height: 110,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        mb: 1.5,
                        '&:hover .camera-overlay': { opacity: 1 },
                      }}
                    >
                      <Avatar
                        src={previewUrl || UserProfile || 'images/avatars/default.png'}
                        sx={{
                          width: 110, height: 110,
                          border: '4px solid',
                          borderColor: previewUrl || (UserProfile && UserProfile !== 'images/avatars/default.png') ? 'rgba(59,130,246,0.25)' : 'rgba(0,0,0,0.06)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                          transition: 'all 0.25s ease',
                        }}
                      />
                      <Box
                        className="camera-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0, left: 0, right: 0, bottom: 0,
                          borderRadius: '50%',
                          bgcolor: 'rgba(0,0,0,0.45)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s ease',
                        }}
                      >
                        <CameraAltOutlinedIcon sx={{ color: '#fff', fontSize: 30 }} />
                      </Box>
                      <input type="file" accept=".jpg,.jpeg,.png" hidden onChange={handleFileChange} />
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
                      {selectedFile ? selectedFile.name : 'Click to change photo'}
                    </Typography>
                  </Box>
                </Grid>

                {/* Form Fields */}
                <Grid item xs={12} md={9}>
                  <Stack spacing={2.5}>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <TextField
                        label="First Name"
                        fullWidth
                        value={UserfName}
                        onChange={(e) => setUserfName(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                      />
                      <TextField
                        label="Last Name"
                        fullWidth
                        value={UserlName}
                        onChange={(e) => setUserlName(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                      />
                    </Box>

                    <TextField
                      label="Email"
                      type="email"
                      fullWidth
                      value={UserEmail}
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                    />

                    <TextField
                      label="Address"
                      fullWidth
                      value={UserAddress}
                      onChange={(e) => setUserAddress(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 500, color: colors.textSecondary, mb: 0.5 }}>Country</Typography>
                        <Select options={countryOptions} value={getDefaultCountryOption()} onChange={handleCountryChange} isClearable placeholder="Select country..." styles={selectStyles} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 500, color: colors.textSecondary, mb: 0.5 }}>State/Province</Typography>
                        <Select
                          options={stateOptions}
                          value={getDefaultStateOption()}
                          onChange={handleStateChange}
                          onInputChange={(inputValue, { action }) => { if (action === 'input-change') triggerStateSearch(inputValue); }}
                          isClearable
                          isLoading={loadingStates}
                          isDisabled={!UserCountry}
                          placeholder={!UserCountry ? "Select country first" : "Search states..."}
                          filterOption={() => true}
                          styles={selectStyles}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 500, color: colors.textSecondary, mb: 0.5 }}>City</Typography>
                        <Select
                          options={cityOptions}
                          value={getDefaultCityOption()}
                          onChange={handleCityChange}
                          onInputChange={(inputValue, { action }) => { if (action === 'input-change') triggerCitySearch(inputValue); }}
                          isClearable
                          isLoading={loadingCities}
                          isDisabled={!UserCountry}
                          placeholder={!UserCountry ? "Select country first" : "Search cities..."}
                          filterOption={() => true}
                          styles={selectStyles}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          label="Zip Code"
                          fullWidth
                          value={UserZip}
                          onChange={(e) => setUserZip(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                        />
                      </Box>
                    </Box>

                    <Box>
                      <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 500, color: colors.textSecondary, mb: 0.5 }}>Timezone</Typography>
                      <Select options={timezoneOptions} value={getDefaultTimezoneOption()} onChange={handleTimezoneChange} isClearable={false} placeholder="Select timezone..." styles={selectStyles} />
                      <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary, mt: 0.5 }}>
                        Helps the AI agent schedule meetings correctly.
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Button
                      variant="contained"
                      onClick={handleSaveButtonClick}
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      sx={{
                        fontFamily,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '12px',
                        px: 4,
                        py: 1.25,
                        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                        boxShadow: '0 4px 16px rgba(59,130,246,0.4)',
                        '&:hover': { background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)', transform: 'translateY(-1px)' },
                        transition: 'all 0.2s ease',
                        alignSelf: 'flex-start',
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>

                    {message && (
                      <Alert severity={message.includes("Error") ? "error" : "success"} sx={{ fontFamily, borderRadius: '12px' }}>
                        {message}
                      </Alert>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Change Password Tab */}
            <TabPanel value={value} index={1}>
              <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 600, color: colors.textPrimary, mb: 3 }}>
                Change Password
              </Typography>
              <ResetPassword />
            </TabPanel>

            {/* Convert Account Tab */}
            <TabPanel value={value} index={2}>
              <AccountConversion />
            </TabPanel>

            {/* Delete Account Tab */}
            <TabPanel value={value} index={3}>
              <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 600, color: colors.error, mb: 2 }}>
                Delete Account
              </Typography>
              <Box sx={{
                bgcolor: 'rgba(220,38,38,0.05)',
                borderRadius: '12px',
                p: 3,
                border: '1px solid rgba(220,38,38,0.15)',
              }}>
                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: colors.textSecondary, mb: 2 }}>
                  Once you delete your account, there is no going back. All of your data, including website content, documents, images, audio, and chatbot data will be permanently erased.
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.875rem', fontWeight: 600, color: colors.error, mb: 3 }}>
                  This action is irreversible. Please be certain.
                </Typography>

                <Button
                  variant="contained"
                  color="error"
                  onClick={handleOpenDeleteDialog}
                  startIcon={<DeleteOutlineIcon />}
                  sx={{
                    fontFamily,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '12px',
                    px: 3,
                    py: 1,
                  }}
                >
                  Delete My Account
                </Button>

                {deleteMessage && (
                  <Alert severity={deleteMessage.includes("Error") ? "error" : "success"} sx={{ mt: 2, fontFamily, borderRadius: '12px' }}>
                    {deleteMessage}
                  </Alert>
                )}
              </Box>
            </TabPanel>
          </Box>
        </Grid>
      </Grid>

      {/* Delete Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
      >
        <DialogTitle sx={{ fontFamily, fontWeight: 600 }}>Are you absolutely sure?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily }}>
            This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog} sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}>Cancel</Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained" sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
